import { motion } from "framer-motion";
import React from "react";
import styled, { css } from "styled-components";

type PageTopSectionProps = {
  children?: React.ReactNode;
  /** Shorter fixed header for dense controls (e.g. Schedule week picker). */
  compact?: boolean;
  /** Minimal-height header (~20% of the default) for pages that need maximum
   *  vertical space for their content (e.g. the Schedule Board grid). */
  slim?: boolean;
  backgroundColor?: string;
  /**
   * Let content overflow the fixed-height header instead of clipping it.
   * Needed when the header hosts an open dropdown/menu that expands downward
   * (e.g. the Social Media Promo "Select Curator" list).
   */
  allowOverflow?: boolean;
  /**
   * Treat the header height as a minimum instead of a fixed size, so content
   * that needs more room (e.g. a filter row that wraps on a narrow window)
   * pushes the header taller instead of being clipped by it.
   */
  autoHeight?: boolean;
};

const headerHeight = ($compact?: boolean, $slim?: boolean): string => {
  if ($slim) return "34px";
  return $compact ? "118px" : "170px";
};

const ContainerPageTopSection = styled.div<{
  $compact?: boolean;
  $slim?: boolean;
  $backgroundColor?: string;
  $allowOverflow?: boolean;
  $autoHeight?: boolean;
}>`
  max-width: 100vw;
  height: ${({ $compact, $slim }): string => headerHeight($compact, $slim)};
  min-height: ${({ $compact, $slim }): string => headerHeight($compact, $slim)};
  max-height: ${({ $compact, $slim }): string => headerHeight($compact, $slim)};
  display: flex;
  flex-direction: column;
  background-color: ${({ $backgroundColor, theme }): string =>
    $backgroundColor ?? theme.colors.darkGray};
  padding: ${({ $compact, $slim, theme }): string =>
      $slim
        ? theme.margins.xsmall
        : $compact
          ? theme.margins.small
          : theme.margins.medium}
    ${(props): string => props.theme.margins.xl};
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: ${({ $allowOverflow }): string =>
    $allowOverflow ? "visible" : "hidden"};

  ${({ $autoHeight }) =>
    $autoHeight &&
    css`
      height: auto;
      max-height: none;
      overflow: visible;

      /* TopBarSection and TopBarColumn size themselves off a 100% height and
         clip what overflows. Once the header grows that height is indefinite,
         so let both size to their content instead. */
      > *,
      > * > * {
        height: auto;
        overflow: visible;
      }
    `}

  @media ${({ theme }) => theme.breakpoints.mobile} {
    height: ${({ $compact, $slim }): string =>
      $slim ? "34px" : $compact ? "104px" : "170px"};
    min-height: ${({ $compact, $slim }): string =>
      $slim ? "34px" : $compact ? "104px" : "170px"};
    max-height: ${({ $compact, $slim }): string =>
      $slim ? "34px" : $compact ? "104px" : "170px"};
    padding: ${({ $slim, theme }): string =>
        $slim ? theme.margins.xsmall : theme.margins.small}
      ${(props): string => props.theme.margins.medium};

    ${({ $autoHeight }) =>
      $autoHeight &&
      css`
        height: auto;
        max-height: none;
      `}
  }
`;

export const TopBarSection = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columnCount}, 1fr);
  width: 100%;
  height: 100%;
  overflow: hidden;
  align-items: center;
  gap: 2.5em;
`;

export const TopBarColumn = styled.div<{ columnCount?: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1rem;
  height: 100%;
  overflow: hidden;
  min-width: ${({ columnCount = 3 }) => `calc(100% / ${columnCount})`};
`;

const PageTopSection = ({
  children,
  compact = false,
  slim = false,
  backgroundColor,
  allowOverflow = false,
  autoHeight = false,
}: PageTopSectionProps): React.JSX.Element => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.3 } }}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
      transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        width: "100%",
      }}
    >
      <ContainerPageTopSection
        $compact={compact}
        $slim={slim}
        $backgroundColor={backgroundColor}
        $allowOverflow={allowOverflow}
        $autoHeight={autoHeight}
      >
        {children}
      </ContainerPageTopSection>
    </motion.div>
  );
};

export default PageTopSection;
